/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {AccountSelector} from '@app/components/accounts/AccountSelector';
import {type Account, SessionExpiredError} from '@app/lib/SessionManager';
import AccountManager from '@app/stores/AccountManager';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useState} from 'react';

interface DesktopHandoffAccountSelectorProps {
	excludeCurrentUser?: boolean;
	onSelectNewAccount: () => void;
	onAccountSelected: (payload: {token: string; userId: string}) => void;
}

const DesktopHandoffAccountSelector = observer(function DesktopHandoffAccountSelector({
	excludeCurrentUser = false,
	onSelectNewAccount,
	onAccountSelected,
}: DesktopHandoffAccountSelectorProps) {
	const {t} = useLingui();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const currentUserId = AccountManager.currentUserId;
	const allAccounts = AccountManager.orderedAccounts;
	const accounts = excludeCurrentUser ? allAccounts.filter((account) => account.userId !== currentUserId) : allAccounts;

	const handleSelectAccount = useCallback(
		async (account: Account) => {
			setIsLoading(true);
			setError(null);

			try {
				const {token, userId} = await AccountManager.generateTokenForAccount(account.userId);
				if (!token) {
					throw new Error('Failed to generate token');
				}
				onAccountSelected({token, userId});
			} catch (err) {
				if (err instanceof SessionExpiredError) {
					setError(t`Session expired. Please log in again.`);
				} else {
					setError(err instanceof Error ? err.message : t`Failed to generate token`);
				}
			} finally {
				setIsLoading(false);
			}
		},
		[onAccountSelected, t],
	);

	return (
		<AccountSelector
			accounts={accounts}
			title={<Trans>Choose an Account</Trans>}
			description={<Trans>Select the account you want to sign in with on the desktop app.</Trans>}
			disabled={isLoading}
			error={error}
			showInstance
			clickableRows
			onSelectAccount={handleSelectAccount}
			onAddAccount={onSelectNewAccount}
			addButtonLabel={<Trans>Add a different account</Trans>}
			scrollerKey="desktop-handoff-scroller"
		/>
	);
});

export default DesktopHandoffAccountSelector;
